"use client";

import MediaImage from "@/components/ui/MediaImage";

import { lockHeaderScroll } from "@/utils/header-menu-scroll";
import { setMenuReveal } from "@/utils/menu-reveal";
import { headerMenuPresentation } from "@/utils/header-menu-presentation";

import { resolveHeaderLogos } from "@/utils/header-logos";
import { schemeLogos } from "@/utils/scheme-logos";
import { isLookSystem } from "@/utils/look-gate";
import { lookSchemeSettings, lookSwitchProps } from "@/utils/look-scheme";
import { withDarkLogo } from "@/utils/look-logo";
import LookSchemeSwitch from "@/components/ui/LookSchemeSwitch";

import IconLabel from "@/icons/IconLabel";
import WebsiteIcon from "@/icons/WebsiteIcon";
import { websiteIcon } from "@/icons/icon-catalogue";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import type { HeaderProps } from "../models";
import { responsiveVariables, surfaceStyle } from "@/utils/visual-style";
import {
  headerSurfaceInDark,
  headerSurfacesInDark,
} from "@/utils/header-colours";
import { resolveFloatingHeaderSettings } from "@/utils/visual-defaults";
import { floatingBarRadius } from "@/utils/header-look";
import { useHeaderState } from "../HeaderStateController";
import { getVisibleValue } from "@/utils/theme-helper";
import { useWebsiteAppearance } from "@/components/ui/AppearanceProvider";
import DesignAction from "@/components/ui/DesignAction";
import SearchBarComponent from "@/components/ui/SearchBarComponent";
import { getPageMenuHref, getPageNavigation } from "@/utils/page-navigation";
import type { CmsPage } from "@/cms/models";
import { resolveNavigation } from "@/utils/header-navigation";
import { resolveHeaderActions } from "@/utils/header-actions";
import HeaderMenu from "@/components/navigation/HeaderMenu";
import HeaderActions from "@/components/navigation/HeaderActions";
import HeaderBottomBar from "@/components/navigation/HeaderBottomBar";
import MenuActions from "@/components/navigation/MenuActions";

function Icon({ name }: { name: "search" | "user" | "menu" | "close" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {name === "search" && (
        <>
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </>
      )}
      {name === "user" && (
        <>
          <path d="M18 20a6 6 0 0 0-12 0" />
          <circle cx="12" cy="10" r="4" />
          <circle cx="12" cy="12" r="10" />
        </>
      )}
      {name === "menu" && <path d="M4 6h16M4 12h16M4 18h16" />}
      {name === "close" && <path d="m6 6 12 12M6 18 18 6" />}
    </svg>
  );
}

function SocialIcon({ name }: { name: string }) {
  const icon = name.toLowerCase();
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icon === "instagram" && (
        <>
          <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
          <path d="M17.5 6.5h.01" />
        </>
      )}
      {icon === "facebook" && (
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      )}
      {icon === "linkedin" && (
        <>
          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
          <rect width="4" height="12" x="2" y="9" />
          <circle cx="4" cy="4" r="2" />
        </>
      )}
      {(icon === "x" || icon === "twitter") && (
        <path d="m4 3 16 18h-4L4 3h4l12 18M20 3 4 21" />
      )}
      {!["instagram", "facebook", "linkedin", "x", "twitter"].includes(
        icon,
      ) && (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a18 18 0 0 1 0 18 18 18 0 0 1 0-18" />
        </>
      )}
    </svg>
  );
}

function PageMenuLink({
  page,
  className,
  onClick,
}: {
  page: CmsPage;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <Link
      className={className}
      href={getPageMenuHref(page)}
      target={page.openInNewTab ? "_blank" : undefined}
      rel={page.openInNewTab ? "noopener noreferrer" : undefined}
      onClick={onClick}
    >
      {page.menuTitle}
    </Link>
  );
}

const OVERLAY_CLOSE_MS = 320;

export default function FloatingHeader({
  cmsData,
  appData,
  header,
  pageHeader,
  logoDefinition,
}: HeaderProps) {
  const options = resolveFloatingHeaderSettings(header.floating);
  const managed = !!useHeaderState();
  const barRadius = isLookSystem(cmsData?.theme)
    ? floatingBarRadius(options.corners)
    : undefined;
  const [panel, setPanel] = useState<"menu" | "search" | null>(null);
  const [closing, setClosing] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLDivElement>(null);
  const appearance = useWebsiteAppearance();
  const ctas = header.ctas ?? [];
  const menu = getPageNavigation(cmsData?.pages ?? []);
  const menuPages = [...menu.primary, ...menu.sidebar];
  const logos = resolveHeaderLogos({
    website: cmsData ?? {},
    definition: logoDefinition,
    config: { ...header, type: "FloatingHeader" },
    media: {
      logo: appData?.logo?.originalUrl,
      inverseLogo: appData?.inverseLogo?.originalUrl,
    },
  });
  const logo = logos.primary.url;
  const inverseLogo = logos.alternative.url;
  const light = appearance?.preference.scheme === "light";
  const schemed = isLookSystem(cmsData?.theme)
    ? null
    : schemeLogos(cmsData?.theme?.appearance, logo, inverseLogo);
  const headerSwitch = lookSwitchProps(cmsData, "header");
  const menuSwitch = lookSwitchProps(cmsData, "menu");
  let portalUrl =
    cmsData?.b2cLink || appData?.resourceGroup?.primaryB2CApplication?.url;
  if (portalUrl && !/^https?:\/\//.test(portalUrl))
    portalUrl = `https://${portalUrl}`;
  const navigation = useMemo(
    () =>
      resolveNavigation({
        site: cmsData,
        pages: cmsData?.pages ?? [],
        type: header.type,
        root: cmsData?.layout?.header,
        page: pageHeader,
        colours: logoDefinition?.colours,
      }),
    [cmsData, header.type, pageHeader, logoDefinition],
  );
  const { bar: barInDark, panel: panelInDark } = headerSurfacesInDark(
    cmsData?.theme,
    { type: header.type, config: cmsData?.layout?.header, page: pageHeader },
    logoDefinition?.colours,
  );
  const actions = useMemo(
    () =>
      Array.isArray(header.actions)
        ? resolveHeaderActions(header.actions, {
            pages: cmsData?.pages ?? [],
            portalUrl,
            theme: cmsData?.theme,
            surfaces: { bar: barInDark, panel: panelInDark },
          })
        : undefined,
    [header.actions, cmsData, portalUrl, barInDark, panelInDark],
  );
  const actionIcons = {
    search: <Icon name="search" />,
    user: <Icon name="user" />,
  };

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (!panel || (navigation && panel === "menu")) {
      if (element.open) element.close();
      return;
    }
    if (!element.open) element.showModal();
    if (panel === "menu") setMenuReveal(drawer.current, menuButton.current);
    closeButton.current?.focus();
    return lockHeaderScroll(document.body);
  }, [panel, navigation]);

  useEffect(() => {
    const onPageChange = () => setPanel(null);
    window.addEventListener("popstate", onPageChange);
    return () => window.removeEventListener("popstate", onPageChange);
  }, []);

  // The menu shrinks back into its button and search fades out before the dialog closes; reduced motion closes at once.
  const close = () => {
    if (!panel || closing) return;
    if (panel === "menu") setMenuReveal(drawer.current, menuButton.current);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches)
      setPanel(null);
    else setClosing(true);
  };
  useEffect(() => {
    if (!closing) return;
    const timer = window.setTimeout(() => {
      setClosing(false);
      setPanel(null);
    }, OVERLAY_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [closing]);

  const brand = (
    <Link href="/" className="re-header-brand" aria-label="Forside">
      {logo && options.logoMode !== "text" ? (
        schemed ? (
          <>
            <MediaImage
              src={schemed.dark}
              alt={options.brandName ?? "Logo"}
              className="re-scheme-dark"
            />
            <MediaImage
              src={schemed.light}
              alt={options.brandName ?? "Logo"}
              className="re-scheme-light"
            />
          </>
        ) : (
          withDarkLogo(
            <MediaImage
              src={light && inverseLogo ? inverseLogo : logo}
              alt={options.brandName ?? "Logo"}
            />,
            lookSchemeSettings(cmsData?.theme)?.logo,
          )
        )
      ) : (
        <span>
          {options.brandName ??
            appData?.resourceGroup?.displayName ??
            "RealEquity"}
        </span>
      )}
    </Link>
  );
  const searchButton = (
    <button
      className={`re-header-icon ${getVisibleValue(header.showSearch)}`}
      aria-label="Søg"
      aria-haspopup="dialog"
      onClick={() => setPanel("search")}
    >
      <Icon name="search" />
    </button>
  );
  const portalLink = portalUrl && (
    <a
      href={portalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`re-header-icon ${getVisibleValue(header.showB2c)}`}
      aria-label="Kundeunivers"
    >
      <Icon name="user" />
    </a>
  );
  const schemeSwitch = menuSwitch ? (
    <LookSchemeSwitch {...menuSwitch} />
  ) : (
    appearance?.settings.allowSchemeSwitch &&
    appearance.settings.palettes?.light &&
    appearance.settings.palettes?.dark && (
      <div>
        <p className="re-drawer-caption">Indstillinger</p>
        <button
          className="re-preference"
          onClick={() =>
            appearance.update({ scheme: light ? "dark" : "light" })
          }
          aria-label="Lyst farvetema"
          aria-pressed={light}
        >
          <span>Tema</span>
          <span>{light ? "☀ Lys" : "☾ Mørk"}</span>
        </button>
      </div>
    )
  );
  const socialLinks = options.socialLinks?.filter(
    (item) => item.iconId !== null,
  );
  const social = !!socialLinks?.length && (
    <div>
      <p className="re-drawer-caption">Følg os</p>
      <div className="re-drawer-social">
        {socialLinks.map((social) => (
          <a
            key={social.url}
            aria-label={social.label}
            title={social.label}
            href={
              /^https?:\/\//i.test(social.url)
                ? social.url
                : `https://${social.url.replace(/^\/+/, "")}`
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            {social.iconId === null ? null : websiteIcon(social.iconId)
                ?.brand ? (
              <WebsiteIcon name={social.iconId} />
            ) : (
              <SocialIcon name={social.icon ?? social.label} />
            )}
          </a>
        ))}
      </div>
    </div>
  );
  const contact = (onClick: () => void) =>
    options.contact && (
      <div className="re-drawer-contact">
        <Link
          className="re-action re-action-pill"
          href={options.contact.link}
          onClick={onClick}
        >
          {options.contact.label}
        </Link>
      </div>
    );
  const panelExtras = (schemeSwitch ||
    social ||
    (!actions && options.contact)) && (
    <div className="re-nav-floating-extras">
      {schemeSwitch}
      {social}
      {!actions && contact(() => undefined)}
    </div>
  );

  return (
    <>
      <header
        className="re-header"
        data-geometry={options.geometry}
        data-angular-fullwidth={options.angularFullWidth}
        data-re-header={managed ? "floating" : undefined}
        style={
          {
            ...responsiveVariables("header-top", options.top),
            ...responsiveVariables("header-inset", options.inset),
            ...responsiveVariables("header-height", options.height),
            ...responsiveVariables("logo-height", options.logoHeight),
            "--re-header-max-width": options.maxWidth,
            "--re-header-link-size": options.linkSize,
            ...(barRadius ? { "--re-header-bar-radius": barRadius } : {}),
            ...(managed
              ? {
                  "--re-header-bar-blur": `${Math.max(0, Math.min(64, options.surface.backdropBlur ?? 0))}px`,
                }
              : {}),
          } as React.CSSProperties
        }
      >
        <div
          ref={bar}
          className="re-header-bar re-surface"
          style={surfaceStyle(
            headerSurfaceInDark(
              cmsData?.theme,
              barRadius
                ? { ...options.surface, radius: undefined }
                : options.surface,
            ),
          )}
        >
          {navigation ? (
            <HeaderMenu
              lookSystem={isLookSystem(cmsData?.theme)}
              open={panel === "menu"}
              onOpenChange={(open) => setPanel(open ? "menu" : null)}
              presentation={headerMenuPresentation(header)}
              navigation={navigation}
              actions={actions}
              legacyActions={
                <>
                  {ctas
                    .filter(
                      (cta: { link: string }) =>
                        !navigation.bar.some(
                          (item) => item.link?.href === cta.link,
                        ),
                    )
                    .map(
                      (cta: { label: string; link: string }, index: number) => (
                        <DesignAction
                          key={index}
                          config={{ ...cta, variant: "quiet" }}
                        />
                      ),
                    )}
                  <div className="re-header-icons">
                    {searchButton}
                    {portalLink}
                  </div>
                </>
              }
              logo={brand}
              bp="md"
              appearance="floating"
              anchor={bar}
              onSearch={() => setPanel("search")}
              icons={actionIcons}
              panelExtras={panelExtras || undefined}
              schemeSwitch={
                headerSwitch && <LookSchemeSwitch {...headerSwitch} />
              }
            />
          ) : (
            <>
              {brand}
              <div className="re-header-actions">
                <nav
                  className="re-header-primary"
                  aria-label="Primær navigation"
                >
                  {menu.primary.map((page) => (
                    <PageMenuLink
                      key={page.id || page.slug}
                      page={page}
                      className="re-action re-action-quiet"
                    />
                  ))}
                  {!actions &&
                    ctas
                      .filter(
                        (cta: { link: string }) =>
                          !menu.primary.some(
                            (page) => getPageMenuHref(page) === cta.link,
                          ),
                      )
                      .map(
                        (
                          cta: { label: string; link: string },
                          index: number,
                        ) => (
                          <DesignAction
                            key={index}
                            config={{ ...cta, variant: "quiet" }}
                          />
                        ),
                      )}
                </nav>
                {actions && (
                  <HeaderActions
                    actions={actions}
                    bp="md"
                    icons={actionIcons}
                    onSearch={() => setPanel("search")}
                  />
                )}
                <div className="re-header-icons">
                  {!actions && searchButton}
                  {!actions && portalLink}
                  {headerSwitch && <LookSchemeSwitch {...headerSwitch} />}
                  <button
                    ref={menuButton}
                    className="re-header-icon"
                    aria-label="Menu"
                    aria-haspopup="dialog"
                    aria-expanded={panel === "menu"}
                    onClick={() => setPanel("menu")}
                  >
                    <Icon name="menu" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </header>
      <dialog
        ref={dialog}
        className="re-native-dialog"
        aria-label={panel === "search" ? "Boligsøgning" : "Navigation"}
        onCancel={(event) => {
          event.preventDefault();
          close();
        }}
        onClose={() => setPanel(null)}
      >
        <div className={closing ? "re-overlay is-closing" : "re-overlay"}>
          <button
            className="re-overlay-backdrop"
            onClick={close}
            aria-label="Luk navigation"
            tabIndex={-1}
          />
          {panel === "menu" ? (
            <div
              ref={drawer}
              className="re-drawer"
              style={
                {
                  "--re-drawer-width": options.drawerWidth,
                } as React.CSSProperties
              }
            >
              <button
                ref={closeButton}
                className="re-header-icon re-overlay-close"
                onClick={close}
                aria-label="Luk menu"
              >
                <Icon name="close" />
              </button>
              <div>
                <p className="re-drawer-caption">Navigation</p>
                <nav className="re-drawer-nav" aria-label="Alle sider">
                  {!actions &&
                    ctas
                      .filter(
                        (cta: { link: string }) =>
                          !menuPages.some(
                            (page) => getPageMenuHref(page) === cta.link,
                          ),
                      )
                      .map(
                        (
                          cta: {
                            link: string;
                            label: string;
                            iconId?: string | null;
                            iconPosition?: "before" | "after";
                          },
                          index: number,
                        ) => (
                          <Link
                            key={`cta-${index}`}
                            href={cta.link}
                            onClick={close}
                          >
                            <IconLabel
                              iconId={cta.iconId}
                              position={cta.iconPosition}
                            >
                              {cta.label}
                            </IconLabel>
                          </Link>
                        ),
                      )}
                  {menu.primary
                    .filter((page) => page.menuPlacement === "primary")
                    .map((page) => (
                      <PageMenuLink
                        key={page.id || page.slug}
                        page={page}
                        className="re-drawer-primary-only"
                        onClick={close}
                      />
                    ))}
                  {menu.sidebar.map((page) => (
                    <PageMenuLink
                      key={page.id || page.slug}
                      page={page}
                      onClick={close}
                    />
                  ))}
                </nav>
              </div>
              {actions && (
                <MenuActions
                  actions={actions}
                  bp="md"
                  icons={actionIcons}
                  onSearch={() => setPanel("search")}
                  onNavigate={close}
                />
              )}
              {schemeSwitch}
              {social}
              {!actions && contact(close)}
            </div>
          ) : (
            <div className="re-search-dialog">
              <button
                ref={closeButton}
                className="re-header-icon re-overlay-close"
                onClick={close}
                aria-label="Luk søgning"
              >
                <Icon name="close" />
              </button>
              <h2>Find din bolig</h2>
              {cmsData && (
                <SearchBarComponent
                  config={{
                    cmsData,
                    variant: "glass",
                    bgColor: "var(--card-bg-color)",
                  }}
                  toggleSearch={close}
                />
              )}
            </div>
          )}
        </div>
      </dialog>
      {actions && (
        <HeaderBottomBar
          actions={actions}
          bp="md"
          icons={actionIcons}
          onSearch={() => setPanel("search")}
        />
      )}
    </>
  );
}
